import { useState, useMemo } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";
import type { Vacation } from "@victor/api-client";
import { api } from "../../lib/api";
import { useAuth } from "../../auth/AuthContext";
import { PointageBlock } from "./PointageBlock";
import { MainCouranteBlock } from "./MainCouranteBlock";
import { RondeBlock } from "./RondeBlock";
import { NetworkBadge } from "./NetworkBadge";
import { PushToggle } from "./PushToggle";

/**
 * Vague 1 · Phase C.1 · Écran "Ma journée".
 *
 * Liste les vacations de l'agent pour le jour sélectionné. Navigation
 * jour précédent / suivant. Click vacation = détail (à venir C.2 :
 * pointage, main courante, etc.).
 */
export function TodayPage() {
  const { user, logout } = useAuth();
  const [day, setDay] = useState(() => startOfDay(new Date()));

  const me = useQuery({
    queryKey: ["me-agent"],
    queryFn: () => api.me.getAgent(),
    retry: false,
  });

  const range = useMemo(() => {
    const from = startOfDay(day).toISOString();
    const to = endOfDay(day).toISOString();
    return { from, to };
  }, [day]);

  const vacations = useQuery({
    queryKey: ["me-vacations", range.from, range.to],
    queryFn: () => api.me.vacations({ from: range.from, to: range.to }),
  });

  const isToday = isSameDay(day, new Date());
  const sorted = useMemo(
    () =>
      [...(vacations.data ?? [])].sort(
        (a, b) => new Date(a.debut).getTime() - new Date(b.debut).getTime(),
      ),
    [vacations.data],
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy-50 to-white">
      {/* Topbar */}
      <header className="sticky top-0 z-10 border-b border-navy-100 bg-white/90 px-5 py-3 backdrop-blur-md">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-accent-500">
              Bonjour {user?.prenom ?? ""}
            </p>
            <h1 className="font-display text-xl font-black tracking-tight text-navy-800">
              Ma journée
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <NetworkBadge />
            <PushToggle />
            <Link
              to="/historique"
              className="rounded-full bg-navy-50 px-3 py-1.5 text-xs font-semibold text-navy-700 transition-colors hover:bg-navy-100"
            >
              Historique
            </Link>
            <button
              type="button"
              onClick={() => void logout()}
              className="rounded-full px-3 py-1.5 text-xs font-semibold text-navy-600 hover:bg-navy-50"
            >
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      {/* Date picker */}
      <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-4">
        <button
          type="button"
          onClick={() => setDay(addDays(day, -1))}
          className="rounded-full bg-white p-3 shadow-sm ring-1 ring-navy-100 hover:bg-navy-50"
          aria-label="Jour précédent"
        >
          ←
        </button>
        <div className="text-center">
          <p className="text-xs font-bold uppercase tracking-wider text-navy-500">
            {isToday ? "Aujourd'hui" : weekday(day)}
          </p>
          <p className="font-display text-lg font-black text-navy-800">
            {formatLongDate(day)}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setDay(addDays(day, 1))}
          className="rounded-full bg-white p-3 shadow-sm ring-1 ring-navy-100 hover:bg-navy-50"
          aria-label="Jour suivant"
        >
          →
        </button>
      </div>
      {!isToday ? (
        <div className="px-4">
          <button
            type="button"
            onClick={() => setDay(startOfDay(new Date()))}
            className="mx-auto block text-xs font-semibold text-accent-600 hover:underline"
          >
            Revenir à aujourd'hui
          </button>
        </div>
      ) : null}

      {/* Contenu */}
      <main className="mx-auto max-w-2xl px-4 pb-12 pt-2">
        {me.isError ? (
          <NoAgentLink />
        ) : vacations.isLoading || me.isLoading ? (
          <SkeletonList />
        ) : sorted.length === 0 ? (
          <EmptyDay />
        ) : (
          <ul className="space-y-3">
            {sorted.map((v, i) => (
              <VacationCard key={v.id} vacation={v} index={i} />
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------

function VacationCard({ vacation, index }: { vacation: Vacation; index: number }) {
  const debut = new Date(vacation.debut);
  const fin = new Date(vacation.fin);
  const duree = Number(vacation.dureeHeures).toFixed(1);
  return (
    <motion.li
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: Math.min(index * 0.05, 0.3) }}
    >
      <article className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-navy-100">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <p className="text-xl font-black tabular-nums text-navy-800">
              {formatHour(debut)} → {formatHour(fin)}
            </p>
            <p className="mt-0.5 text-xs text-navy-500">{duree} h</p>
            {vacation.posteLibelle ? (
              <p className="mt-2 text-sm font-bold text-navy-800">
                {vacation.posteLibelle}
              </p>
            ) : null}
            {vacation.consignes ? (
              <p className="mt-1 text-sm text-navy-600">{vacation.consignes}</p>
            ) : null}
          </div>
          <StatutBadge statut={vacation.statut} />
        </div>
        <PointageBlock vacation={vacation} />
        <MainCouranteBlock vacation={vacation} />
        <RondeBlock vacation={vacation} />
      </article>
    </motion.li>
  );
}

function StatutBadge({ statut }: { statut: Vacation["statut"] }) {
  const map: Record<Vacation["statut"], { label: string; cls: string }> = {
    pourvoir: { label: "À pourvoir", cls: "bg-amber-100 text-amber-700" },
    affectee: { label: "Affectée", cls: "bg-accent-50 text-accent-700" },
    terminee: { label: "Terminée", cls: "bg-green-100 text-green-700" },
    annulee: { label: "Annulée", cls: "bg-navy-100 text-navy-500" },
  };
  const { label, cls } = map[statut];
  return (
    <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${cls}`}>
      {label}
    </span>
  );
}

function EmptyDay() {
  return (
    <div className="rounded-2xl bg-white p-8 text-center shadow-sm ring-1 ring-navy-100">
      <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-accent-50 p-3 text-accent-500">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </div>
      <p className="font-display text-lg font-black text-navy-800">
        Aucune vacation ce jour
      </p>
      <p className="mt-1 text-sm text-navy-500">
        Profitez de votre journée !
      </p>
    </div>
  );
}

function NoAgentLink() {
  return (
    <div className="rounded-2xl bg-amber-50 p-6 text-center ring-1 ring-amber-200">
      <p className="font-bold text-amber-800">
        Compte non associé à un agent terrain
      </p>
      <p className="mt-2 text-sm text-amber-700">
        Demandez à votre planificateur de relier votre compte utilisateur à
        votre profil agent.
      </p>
    </div>
  );
}

function SkeletonList() {
  return (
    <ul className="space-y-3">
      {[0, 1, 2].map((i) => (
        <li
          key={i}
          className="h-24 animate-pulse rounded-2xl bg-white shadow-sm ring-1 ring-navy-100"
        />
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------

function startOfDay(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}
function endOfDay(d: Date): Date {
  const r = new Date(d);
  r.setHours(23, 59, 59, 999);
  return r;
}
function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function formatLongDate(d: Date): string {
  return d.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}
function weekday(d: Date): string {
  return d.toLocaleDateString("fr-FR", { weekday: "long" });
}
function formatHour(d: Date): string {
  return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}
